/**
 * Idea-Teasers — alle Phase-2/3-Ideen kompakt als Mock-Cards.
 * "Mindestens angeteasert" pro Daniels Anforderung.
 */
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Calculator,
  Camera,
  Clock,
  Cpu,
  Download,
  FileLock2,
  GitBranch,
  Headphones,
  Heart,
  KeyRound,
  ListTree,
  MapPin,
  MessageCircle,
  Repeat,
  ScrollText,
  Send,
  Sparkles,
  Terminal as TerminalIcon,
  Wifi,
  Zap,
} from "lucide-react";

export function TeaserCard({
  title,
  description,
  icon,
  phase,
  children,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  phase: "MVP" | "Phase 1" | "Phase 2" | "Phase 3";
  children?: React.ReactNode;
}) {
  const phaseColor =
    phase === "MVP"
      ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
      : phase === "Phase 1"
        ? "text-sky-300 border-sky-500/40 bg-sky-500/10"
        : phase === "Phase 2"
          ? "text-amber-300 border-amber-500/40 bg-amber-500/10"
          : "text-zinc-400 border-zinc-700 bg-zinc-800/40";

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 hover:border-zinc-700 transition-colors">
      <div className="flex items-start gap-3">
        <div className="text-zinc-400 mt-0.5">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-zinc-100">{title}</h4>
            <span className={`text-[9px] font-mono px-1.5 py-0.5 border rounded ${phaseColor}`}>
              {phase}
            </span>
          </div>
          <p className="text-xs text-zinc-400 mt-0.5 leading-relaxed">{description}</p>
          {children && <div className="mt-2">{children}</div>}
        </div>
      </div>
    </div>
  );
}

export function MockTopology() {
  return (
    <TeaserCard
      title="Live Topology"
      description="IPSC-Master/Peer-Graph mit Live-PA-Temp + VSWR + Online-Status. Click pro Node öffnet Repeater-Detail."
      icon={<Repeat className="h-4 w-4" />}
      phase="Phase 1"
    >
      <div className="rounded border border-zinc-800 bg-zinc-950/60 p-2 font-mono text-[10px] text-zinc-300">
        <div className="flex items-center justify-center gap-6 py-2">
          <Node label="REP-01" sub="master · 38.2°C" online />
          <Arrow />
          <Node label="REP-02" sub="peer · 41.7°C" online />
        </div>
        <div className="flex items-center justify-center gap-6">
          <Node label="REP-03" sub="peer · 73.4°C !" offline />
          <Arrow />
          <Node label="REP-04" sub="peer · 39.1°C" online />
        </div>
      </div>
    </TeaserCard>
  );
}

function Node({ label, sub, online, offline }: { label: string; sub: string; online?: boolean; offline?: boolean }) {
  return (
    <span
      className={`inline-flex flex-col items-center px-3 py-1.5 rounded border ${
        online ? "border-emerald-500/40 bg-emerald-500/5" : offline ? "border-red-500/40 bg-red-500/5" : "border-zinc-700"
      }`}
    >
      <span className="text-zinc-100 font-semibold">{label}</span>
      <span className={`text-[9px] ${online ? "text-emerald-400" : "text-red-400"}`}>{sub}</span>
    </span>
  );
}
function Arrow() {
  return <span className="text-zinc-700">───</span>;
}

export function MockSshTerminal() {
  return (
    <TeaserCard
      title="Mock SSH Terminal"
      description="Read-only Diagnose pro Repeater. Echte MOTOTRBO-Commands wie Engineer sie absetzen würde."
      icon={<TerminalIcon className="h-4 w-4" />}
      phase="Phase 3"
    >
      <pre className="text-[10px] text-emerald-300 bg-zinc-950 border border-zinc-800 rounded p-2 font-mono overflow-hidden">
{`mototrbo@PAD2-REP-01:~$ show ipsc status
  IPSC Network ID:  8
  Master:          this (192.168.0.11)
  Peers:           3/3 connected
  Slot 1:          active (TG=42, RSSI=-58dBm)
  Slot 2:          idle
mototrbo@PAD2-REP-01:~$ show pa temperature
  Current:  38.2°C
  Peak 24h: 47.6°C @ 14:12`}
      </pre>
    </TeaserCard>
  );
}

export function MockLogTail() {
  return (
    <TeaserCard
      title="Live Log Tail"
      description="kubectl logs -f für Repeater. Call Start/End, Voltage Alarm, Slot-Utilization in Echtzeit."
      icon={<ScrollText className="h-4 w-4" />}
      phase="Phase 2"
    >
      <pre className="text-[10px] bg-zinc-950 border border-zinc-800 rounded p-2 font-mono leading-relaxed">
        <span className="text-zinc-600">14:32:41 </span>
        <span className="text-emerald-400">call.end</span>
        <span className="text-zinc-300"> rep=01 ts=1 rid=4711 dur=12.4s</span>
        {"\n"}
        <span className="text-zinc-600">14:32:38 </span>
        <span className="text-sky-400">call.start</span>
        <span className="text-zinc-300"> rep=01 ts=1 rid=4711 tg=42</span>
        {"\n"}
        <span className="text-zinc-600">14:31:55 </span>
        <span className="text-amber-400">pa.temp.warn</span>
        <span className="text-zinc-300"> rep=03 t=73.4°C</span>
        {"\n"}
        <span className="text-zinc-600">14:31:50 </span>
        <span className="text-red-400">ipsc.peer.disconnect</span>
        <span className="text-zinc-300"> peer=192.168.0.13</span>
      </pre>
    </TeaserCard>
  );
}

export function MockLossBudget() {
  return (
    <TeaserCard
      title="Loss-Budget Calculator"
      description="Click eine Antenne im Layout — sehe Signal-Loss von Repeater bis Antenne (Coax + Splitter + Tap)."
      icon={<Calculator className="h-4 w-4" />}
      phase="Phase 2"
    >
      <div className="text-[11px] font-mono space-y-1 bg-zinc-950 border border-zinc-800 rounded p-2">
        <Row k="REP-01" v="+36.0 dBm" />
        <Row k="LCF7/8 78m" v="−2.9 dB" />
        <Row k="Splitter 4-way" v="−6.0 dB" />
        <Row k="Tap PRO-TAP" v="−8.0 dB" />
        <Row k="LCF1/2 11m" v="−0.5 dB" />
        <Row k="ANT-13" v="+18.6 dBm" bold tone="ok" />
      </div>
    </TeaserCard>
  );
}

function Row({ k, v, bold, tone }: { k: string; v: string; bold?: boolean; tone?: "ok" }) {
  return (
    <div className={`flex justify-between ${bold ? "border-t border-zinc-800 pt-1 mt-1 font-semibold" : ""}`}>
      <span className="text-zinc-500">{k}</span>
      <span className={tone === "ok" ? "text-emerald-300" : "text-zinc-300"}>{v}</span>
    </div>
  );
}

export function MockCompliance() {
  return (
    <TeaserCard
      title="Compliance Dashboard"
      description="EU-weite Übersicht: Frequenzen ablaufend, Codeplug-Drift, Survey-Overdue, Repeater-Health. Marcels Daily-Driver."
      icon={<AlertTriangle className="h-4 w-4" />}
      phase="Phase 1"
    >
      <div className="grid grid-cols-2 gap-1.5 text-[11px] font-mono">
        <Stat label="Freq < 30d" value="3" tone="err" />
        <Stat label="Freq < 90d" value="12" tone="warn" />
        <Stat label="Codeplug drift" value="7" tone="warn" />
        <Stat label="Survey > 365d" value="47" tone="warn" />
        <Stat label="Repeater offline" value="1" tone="err" />
        <Stat label="Healthy sites" value="186/234" tone="ok" />
      </div>
    </TeaserCard>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone: "ok" | "warn" | "err" }) {
  const cls = tone === "ok" ? "text-emerald-300" : tone === "warn" ? "text-amber-300" : "text-red-300";
  return (
    <div className="rounded border border-zinc-800 bg-zinc-950/40 px-2 py-1 flex justify-between items-baseline">
      <span className="text-zinc-500">{label}</span>
      <span className={`font-semibold ${cls}`}>{value}</span>
    </div>
  );
}

export function MockHealthScore() {
  return (
    <TeaserCard
      title="Site Health Score"
      description="Single-Number 0-100 pro Site. Frequency + Codeplug + Hardware-Age + Survey-Recency + Open-Issues. EU-Liste sortierbar."
      icon={<Heart className="h-4 w-4" />}
      phase="Phase 1"
    >
      <div className="flex items-end gap-3">
        <span className="text-4xl font-mono font-bold text-emerald-300">87</span>
        <div className="text-[10px] font-mono text-zinc-500 pb-1.5 leading-tight">
          <div>/ 100</div>
          <div className="text-zinc-600">EU rank: 42 / 234</div>
        </div>
      </div>
    </TeaserCard>
  );
}

export function MockCodeplugDiff() {
  return (
    <TeaserCard
      title="Codeplug Diff View"
      description="GitHub-Style Diff zwischen Site-Standard und Live-Codeplug. One-Click 'Apply Standard'."
      icon={<GitBranch className="h-4 w-4" />}
      phase="Phase 1"
    >
      <pre className="text-[10px] font-mono bg-zinc-950 border border-zinc-800 rounded p-2 leading-relaxed">
        <span className="text-zinc-500">  contact.list[42] {"{"}</span>
        {"\n"}
        <span className="text-red-400">- name: &quot;Loss Prev&quot;</span>
        {"\n"}
        <span className="text-emerald-400">+ name: &quot;Loss Prevention&quot;</span>
        {"\n"}
        <span className="text-zinc-500">    type: group_call</span>
        {"\n"}
        <span className="text-zinc-500">  {"}"}</span>
      </pre>
    </TeaserCard>
  );
}

export function MockSecretVault() {
  return (
    <TeaserCard
      title="Secret Vault with Reveal Audit"
      description="AES-Keys & Passwords envelope-encrypted (KMS). Reveal triggert Audit + Mattermost-Notify + 30s Re-Hide. Phase-2: Post-Quantum Layer (ML-KEM)."
      icon={<KeyRound className="h-4 w-4" />}
      phase="Phase 1"
    >
      <div className="text-[11px] font-mono space-y-1">
        <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded px-2 py-1">
          <span className="text-zinc-400">AES-256-KEY · TS1+TS2</span>
          <span className="text-zinc-700">••••••••••••••••</span>
          <button className="text-[9px] text-emerald-300 hover:underline">reveal</button>
        </div>
        <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded px-2 py-1">
          <span className="text-zinc-400">CPS Programming Password</span>
          <span className="text-zinc-700">••••••••</span>
          <button className="text-[9px] text-emerald-300 hover:underline">reveal</button>
        </div>
      </div>
    </TeaserCard>
  );
}

export function MockR7Provision() {
  return (
    <TeaserCard
      title="R7 Provisioning Workflow"
      description="Techniker scannt R7-S/N, wählt Site, klickt 'Generate Codeplug' → Download mit Site-Standard + AES-Key + QR-Code."
      icon={<Zap className="h-4 w-4" />}
      phase="Phase 2"
    >
      <div className="text-[10px] font-mono bg-zinc-950 border border-zinc-800 rounded p-2">
        <span className="text-emerald-400">$ amzel provision r7 --site PAD2 --serial 478JKL</span>
        {"\n"}
        <span className="text-zinc-400">  → generating codeplug v1.4.2…</span>
        {"\n"}
        <span className="text-zinc-400">  → embedding AES-256 key (kms:eu-central-1/...)</span>
        {"\n"}
        <span className="text-zinc-400">  → QR for Motorola CPS app generated</span>
        {"\n"}
        <span className="text-emerald-400">  ✓ ready: PAD2_R7_478JKL_v1.4.2.zip (also QR)</span>
      </div>
    </TeaserCard>
  );
}

export function MockAuditPack() {
  return (
    <TeaserCard
      title="Audit Pack Export"
      description="One-Click ZIP für AMZ-Infosec mit allen Configs, Cert-Status, Compliance, Audit-Logs pro Site."
      icon={<FileLock2 className="h-4 w-4" />}
      phase="Phase 2"
    >
      <button className="text-[11px] font-mono px-2 py-1.5 border border-zinc-700 rounded bg-zinc-900 hover:bg-zinc-800 hover:border-zinc-600 text-zinc-200 flex items-center gap-2">
        <Download className="h-3 w-3" />
        Generate audit pack for PAD2
      </button>
    </TeaserCard>
  );
}

export function MockSpotifyWrapped() {
  return (
    <TeaserCard
      title="Spotify-Wrapped Site Report"
      description="Per-Site Jahres-KPIs als visuelle Card: #Calls, Top-Talker, Avg PA-Temp, Outages. Quarterly Review Material."
      icon={<Sparkles className="h-4 w-4" />}
      phase="Phase 3"
    >
      <div className="grid grid-cols-3 gap-1 text-[10px] font-mono">
        <Mini k="calls 2025" v="124,802" />
        <Mini k="avg PA °C" v="41.3" />
        <Mini k="outages" v="3 · 8.2m" />
      </div>
    </TeaserCard>
  );
}
function Mini({ k, v }: { k: string; v: string }) {
  return (
    <div className="rounded bg-zinc-950 border border-zinc-800 p-1.5">
      <div className="text-zinc-600 text-[9px]">{k}</div>
      <div className="text-emerald-300 font-semibold">{v}</div>
    </div>
  );
}

export function MockFieldPhotos() {
  return (
    <TeaserCard
      title="Field-Photo Timeline"
      description="Install/Wartung/Updates als visuelle Timeline. Spreenauten zeigt: 'Look how PAD2 evolved over 2 years.'"
      icon={<Camera className="h-4 w-4" />}
      phase="Phase 3"
    >
      <div className="flex gap-1.5 overflow-hidden">
        {["2024-02", "2024-08", "2025-06", "2026-03", "2026-05"].map((d) => (
          <div
            key={d}
            className="shrink-0 w-16 h-12 rounded border border-zinc-800 bg-gradient-to-br from-zinc-800 to-zinc-950 flex items-end p-1"
          >
            <span className="text-[8px] font-mono text-zinc-500">{d}</span>
          </div>
        ))}
      </div>
    </TeaserCard>
  );
}

export function MockCoveragePredictor() {
  return (
    <TeaserCard
      title="Real-Time Coverage Predictor"
      description="Antenne im Layout virtuell verschieben — sehe live geänderte Coverage-Heatmap (FSPL + Wall-Attenuation)."
      icon={<Wifi className="h-4 w-4" />}
      phase="Phase 3"
    />
  );
}

export function MockSelfService() {
  return (
    <TeaserCard
      title="Self-Service Buttons (AMZ-side)"
      description="AMZ klickt → wir kriegen Jira-Ticket: Request Frequency Renewal · Order R7 Pre-Configured · Schedule Maintenance Visit · Request Site Survey."
      icon={<Send className="h-4 w-4" />}
      phase="Phase 2"
    />
  );
}

export function MockSlackBot() {
  return (
    <TeaserCard
      title="Slack/Teams Bot"
      description="/amzel site PAD2 in AMZ-internal channels. Embeds via OpenAPI für AMZ-Q-Kompatibilität."
      icon={<MessageCircle className="h-4 w-4" />}
      phase="Phase 2"
    />
  );
}

export function MockCrewTracking() {
  return (
    <TeaserCard
      title="Crew Tracking"
      description="Wer war wann auf welcher Site (Install/Maintenance/Survey). Andrea kennt PAD2 inside-out."
      icon={<ListTree className="h-4 w-4" />}
      phase="MVP"
    />
  );
}

export function MockSiteComparison() {
  return (
    <TeaserCard
      title="Site Comparison"
      description="PAD2 vs BER3 vs LEJ5 side-by-side wie Feature-Tabelle. AMZ sieht granulare Daten."
      icon={<BarChart3 className="h-4 w-4" />}
      phase="Phase 2"
    />
  );
}

export function MockComplianceCal() {
  return (
    <TeaserCard
      title="Compliance Calendar (iCal)"
      description="Stichtage pro Site als iCal-Feed. AMZ subscribed in Outlook/Google Cal. Push-Reminder ohne Login."
      icon={<Clock className="h-4 w-4" />}
      phase="Phase 2"
    />
  );
}

export function MockApiSnippets() {
  return (
    <TeaserCard
      title="API + curl Snippets"
      description="Embedded curl-Block auf jeder Seite. AMZ-Q-Kompatibilität als Nebeneffekt."
      icon={<Cpu className="h-4 w-4" />}
      phase="MVP"
    >
      <pre className="text-[10px] font-mono bg-zinc-950 border border-zinc-800 rounded p-2 text-zinc-300">
{`curl -H "Authorization: Bearer $TOKEN" \\
  https://api.amzel.io/v1/dossier/sites/PAD2`}
      </pre>
    </TeaserCard>
  );
}

export function MockDrPlaybook() {
  return (
    <TeaserCard
      title="Disaster Recovery Playbook"
      description="Auto-generiert pro Site: Mini-PC tot · 1 Repeater tot · ganze Site offline — step-by-step mit echten IPs/Pfaden."
      icon={<Activity className="h-4 w-4" />}
      phase="Phase 2"
    />
  );
}

export function MockHeatmapViewer() {
  return (
    <TeaserCard
      title="Heatmap-Viewer mit Layern"
      description="Static Survey-Heatmap + Live-Overlay aus Edge-Agent. Toggle Indoor/Outdoor/Floor."
      icon={<MapPin className="h-4 w-4" />}
      phase="Phase 1"
    />
  );
}

export function MockManDownGeofence() {
  return (
    <TeaserCard
      title="Man-Down Geofence Map (für Ryck)"
      description="Coverage-Polygone aus Heatmap-Daten + Geofence-Zonen. Ryck's Man-Down dockt hier an."
      icon={<Headphones className="h-4 w-4" />}
      phase="Phase 2"
    />
  );
}
